import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Button, Modal, ModalHeader, ModalBody, ModalFooter,
  Form, FormGroup, Label, Input, CustomInput
} from 'reactstrap';
import { ALL_STEPS } from '../../components/ProcessStep/steps';
import { EMAIL_TONES } from '../../constants/emailScenarios';
import { getScenarioOptions } from '../../components/AIEmailAssistant/scenarios';
import resolvePlaceholders from '../../components/AIEmailAssistant/placeholders';

const getDefaultValues = () => ({
  name: '',
  funnel_stage: 'any',
  scenario: 'follow_up',
  language: 'it',
  tone: 'professional',
  subject_template: '',
  body_template: '',
  tags: [],
});

const parseTags = (value = '') => value
  .split(',')
  .map(tag => tag.trim())
  .filter(Boolean);

const normalizeTagsInput = (tags = []) => Array.isArray(tags) ? tags.join(', ') : `${tags || ''}`;

const sampleContext = (form) => ({
  deal: {
    name: 'Annual renewal',
    stage: form.funnel_stage === 'any' ? 'contract_sent' : form.funnel_stage,
    amount: 12400,
    closing_date: '2026-07-15',
    success_rate: 60,
  },
  company: {
    name: 'Acme S.r.l.',
  },
  contact: {
    first_name: 'Mario',
    last_name: 'Rossi',
    full_name: 'Mario Rossi',
    email: 'mario.rossi@acme.it',
  },
  user: {
    full_name: 'Francesco Falanga',
  },
});

const TEMPLATE_VARIABLES = [
  '{{contact.first_name}}',
  '{{contact.last_name}}',
  '{{contact.full_name}}',
  '{{company.name}}',
  '{{deal.name}}',
  '{{deal.amount}}',
  '{{deal.stage}}',
  '{{deal.closing_date}}',
  '{{deal.success_rate}}',
  '{{user.full_name}}',
  '{{today}}',
];

function TemplateEditorModal({ open, toggle, template, onSave, isSaving }) {
  const [t] = useTranslation('EMAIL_TEMPLATES');
  const [aiT] = useTranslation('AI_EMAIL_ASSISTANT');
  const [commonT] = useTranslation('COMMON');

  const [form, setForm] = useState(getDefaultValues());
  const [tagsInput, setTagsInput] = useState('');

  useEffect(() => {
    if (!open) return;

    const nextForm = template ? {
      name: template.name || '',
      funnel_stage: template.funnel_stage || 'any',
      scenario: template.scenario || 'follow_up',
      language: template.language || 'it',
      tone: template.tone || 'professional',
      subject_template: template.subject_template || '',
      body_template: template.body_template || '',
      tags: template.tags || [],
    } : getDefaultValues();

    setForm(nextForm);
    setTagsInput(normalizeTagsInput(nextForm.tags));
  }, [open, template]);

  const previewContext = useMemo(() => sampleContext(form), [form]);
  const previewSubject = resolvePlaceholders(form.subject_template, previewContext);
  const previewBody = resolvePlaceholders(form.body_template, previewContext);
  const canSave = form.name && form.subject_template && form.body_template;

  const setField = (field, value) => setForm(current => ({ ...current, [field]: value }));

  const handleSubmit = async (event) => {
    event.preventDefault();

    const payload = {
      ...form,
      tags: parseTags(tagsInput),
    };

    const success = await onSave(payload);
    if (success) toggle();
  };

  const title = template ? t('EDIT_TEMPLATE') : t('NEW_TEMPLATE');

  return (
    <Modal isOpen={open} fade={false} toggle={toggle} size="xl" className="template-editor-modal">
      <ModalHeader toggle={toggle}>{title}</ModalHeader>
      <ModalBody>
        <Form onSubmit={handleSubmit}>
          <div className="template-editor-stack">
            <FormGroup>
              <Label>{t('NAME')}</Label>
              <Input value={form.name} onChange={ev => setField('name', ev.target.value)} />
            </FormGroup>
            <FormGroup>
              <Label>{t('FUNNEL_STAGE')}</Label>
              <CustomInput id="email-template-funnel-stage" type="select" value={form.funnel_stage} onChange={ev => setField('funnel_stage', ev.target.value)}>
                <option value="any">{t('ANY')}</option>
                {ALL_STEPS.map(step => (
                  <option key={step} value={step}>{commonT(`DEALS_STATUSES_${step.toUpperCase()}`)}</option>
                ))}
              </CustomInput>
            </FormGroup>
            <FormGroup>
              <Label>{t('SCENARIO')}</Label>
              <CustomInput id="email-template-scenario" type="select" value={form.scenario} onChange={ev => setField('scenario', ev.target.value)}>
                <option value="custom">{t('CUSTOM')}</option>
                {getScenarioOptions().map(option => (
                  <option key={option} value={option}>{aiT(`SCENARIOS.${option}`)}</option>
                ))}
              </CustomInput>
            </FormGroup>
            <FormGroup>
              <Label>{t('LANGUAGE')}</Label>
              <CustomInput id="email-template-language" type="select" value={form.language} onChange={ev => setField('language', ev.target.value)}>
                <option value="it">{aiT('LANGUAGES.it')}</option>
                <option value="en">{aiT('LANGUAGES.en')}</option>
              </CustomInput>
            </FormGroup>
            <FormGroup>
              <Label>{t('TONE')}</Label>
              <CustomInput id="email-template-tone" type="select" value={form.tone} onChange={ev => setField('tone', ev.target.value)}>
                {EMAIL_TONES.map(option => (
                  <option key={option} value={option}>{aiT(`TONES.${option}`)}</option>
                ))}
              </CustomInput>
            </FormGroup>
            <FormGroup>
              <Label>{t('TAGS')}</Label>
              <Input
                value={tagsInput}
                onChange={ev => setTagsInput(ev.target.value)}
                placeholder={t('TAGS_PLACEHOLDER')}
              />
            </FormGroup>
            <FormGroup>
              <Label>{t('SUBJECT_TEMPLATE')}</Label>
              <Input
                value={form.subject_template}
                onChange={ev => setField('subject_template', ev.target.value)}
                placeholder="{{company.name}}"
              />
            </FormGroup>
            <FormGroup>
              <Label>{t('BODY_TEMPLATE')}</Label>
                <Input
                  type="textarea"
                  className="template-body-input"
                  value={form.body_template}
                  onChange={ev => setField('body_template', ev.target.value)}
                  placeholder="{{contact.first_name}}"
                />
            </FormGroup>
            <div className="template-variable-help">
              <h6>{t('VARIABLES_TITLE')}</h6>
              <p>{t('VARIABLES_HELP')}</p>
              <div className="template-variable-list">
                {TEMPLATE_VARIABLES.map(variable => <code key={variable}>{variable}</code>)}
              </div>
            </div>
            <div className="template-preview">
              <h6>{t('PREVIEW')}</h6>
              <p className="template-preview-help">{t('PREVIEW_HELP')}</p>
              <div className="template-preview-block">
                <span>{t('SUBJECT_TEMPLATE')}</span>
                <strong>{previewSubject || '-'}</strong>
              </div>
              <div className="template-preview-block">
                <span>{t('BODY_TEMPLATE')}</span>
                <pre>{previewBody || '-'}</pre>
              </div>
            </div>
          </div>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>{commonT('CANCEL')}</Button>
        <Button color="primary" onClick={handleSubmit} disabled={!canSave || isSaving}>{isSaving ? commonT('LOADING') : (template ? t('UPDATE') : t('SAVE'))}</Button>
      </ModalFooter>
    </Modal>
  );
}

TemplateEditorModal.propTypes = {
  open: PropTypes.bool,
  toggle: PropTypes.func.isRequired,
  template: PropTypes.shape({}),
  onSave: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
};

TemplateEditorModal.defaultProps = {
  open: false,
  template: null,
  isSaving: false,
};

export default TemplateEditorModal;
